import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Field } from "@base-ui/react/field";
import { Popover } from "@base-ui/react/popover";
import { Form } from "@base-ui/react/form";
import { Plus } from "lucide-react";
import { EntityStatus, EntityType } from "@repo/types/enums";
import type { AddUpdateDataViewViewModel } from "../../types/viewModels/dataViews";
import { useAddUpdateDataView } from "../../hooks/queries";
import Button from "../ui/button/Button";

export interface AddDataViewProps {
  entityType: EntityType;
}

export default function AddDataView<T>({ entityType }: AddDataViewProps) {
  const { mutateAsync: addUpdateDataView } = useAddUpdateDataView<T>();

  const { control, handleSubmit, reset } = useForm<
    AddUpdateDataViewViewModel<T>
  >({
    defaultValues: {
      Name: "",
      EntityType: entityType,
      Status: EntityStatus.Active,
    },
  });

  const [isSaveViewOpen, setIsSaveViewOpen] = useState(false);

  async function handleSaveView(data: AddUpdateDataViewViewModel<T>) {
    await addUpdateDataView(data);
    setIsSaveViewOpen(false);
  }

  return (
    <Popover.Root
      open={isSaveViewOpen}
      onOpenChange={(open) => {
        setIsSaveViewOpen(open);
        if (!open) {
          reset();
        }
      }}
    >
      <Popover.Trigger className="flex items-center space-x-1 pl-2 pr-2 pt-1 pb-1 text-sm cursor-pointer hover:bg-gray-100 rounded data-popup-open:bg-blue-200">
        <Plus size={13} /> <div>Add view</div>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Positioner sideOffset={8} align="start">
          <Popover.Popup
            className={`rounded p-3 bg-white border border-gray-200 shadow-xl focus:outline-none`}
          >
            <Form onSubmit={handleSubmit(handleSaveView)}>
              <div className="">
                <Controller
                  name="Name"
                  control={control}
                  rules={{
                    required: "View name is required",
                  }}
                  render={({
                    field: { value, onChange, onBlur, ref },
                    fieldState: { invalid, isTouched, isDirty, error },
                  }) => (
                    <Field.Root
                      className="flex w-full flex-col items-start gap-1"
                      name="Name"
                      invalid={!!invalid}
                      touched={isTouched}
                      dirty={isDirty}
                    >
                      <Field.Control
                        required
                        placeholder="View name"
                        type="text"
                        value={value}
                        onBlur={onBlur}
                        onValueChange={onChange}
                        className="h-8 w-full rounded border border-gray-200 pl-1 text-sm text-gray-900"
                        ref={ref}
                      />
                      <Field.Error
                        className="text-xs text-red-800"
                        match={!!error}
                      >
                        {error?.message}
                      </Field.Error>
                    </Field.Root>
                  )}
                />
              </div>

              <div className="flex justify-end space-x-2 items-center mt-4">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setIsSaveViewOpen(false)}
                >
                  Close
                </Button>

                <Button type="submit" size="sm">
                  Save
                </Button>
              </div>
            </Form>
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}
